"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Template } from "@/lib/api";
import { Copy, Check, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import Image from "next/image";
import { getTemplateLibraryMeta, getTemplateSecondaryTags } from "@/lib/template-library";
import { cn } from "@/lib/utils";

interface TemplateDetailSheetProps {
  template: Template | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUse: (template: Template) => void;
  useLabel?: string;
}

export function TemplateDetailSheet({
  template,
  open,
  onOpenChange,
  onUse,
  useLabel = "Использовать",
}: TemplateDetailSheetProps) {
  const [copied, setCopied] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    setCopied(false);
    setIsPromptExpanded(false);
    setVideoLoading(true);
    setVideoFailed(false);
  }, [template?.id, open]);

  if (!template) return null;

  const isVideoUrl = (url: string) => /\.(mp4|webm|mov)(\?|$)/i.test(url);
  const imageSrc = (() => {
    const cover = template.cover_url || "";
    const preview = template.preview_url || "";
    if (cover && !isVideoUrl(cover)) return cover;
    if (preview && !isVideoUrl(preview)) return preview;
    return "";
  })();
  const isEffect = (template.type || "").toLowerCase() === "motion";
  const isVideoType = (template.type || "").toLowerCase() === "video" || isEffect;
  const videoSrc = (() => {
    const preview = template.preview_url || "";
    const cover = template.cover_url || "";
    if (isVideoUrl(preview)) return preview;
    if (isVideoUrl(cover)) return cover;
    // For video/effect templates, try any URL (CDN may serve video without extension)
    if (isVideoType) return preview || cover || "";
    return "";
  })();
  const libraryMeta = getTemplateLibraryMeta(template);
  const templateTags = getTemplateSecondaryTags(template).filter((tag) => {
    const normalizedTag = tag.trim().toLowerCase();
    const normalizedModel = (libraryMeta.modelLabel || "").trim().toLowerCase();
    return !normalizedModel || normalizedTag !== normalizedModel;
  });
  const rawPrompt = template.prompt?.base || "";
  // Guard against JSON objects stored as prompt text
  const isJsonPrompt = rawPrompt.trimStart().startsWith("{") || rawPrompt.trimStart().startsWith("[");
  const hasRealPrompt = rawPrompt.length > 0 && !rawPrompt.includes("{prompt}") && !isEffect && !isJsonPrompt;
  const promptText = hasRealPrompt ? rawPrompt : "";
  const canExpandPrompt = promptText.length > 200;
  const typeLabel =
    (template.type || "").toLowerCase() === "video"
      ? "Видео"
      : (template.type || "").toLowerCase() === "image"
        ? "Изображение"
        : (template.type || "").toLowerCase() === "motion"
          ? "Эффект"
          : template.type;

  const handleCopyPrompt = async () => {
    if (!promptText) return;
    try {
      await navigator.clipboard.writeText(promptText);
      setCopied(true);
      toast.success("Промпт скопирован");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Не удалось скопировать промпт");
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange} title={template?.title || "Шаблон"} className="sm:w-[calc(100vw-2rem)] sm:max-w-lg max-h-[85vh] overflow-y-auto p-0 bg-background/95 backdrop-blur-xl border-border">
        {/* Cover Image / Video */}
        {(imageSrc || videoSrc) && (
          <div className="relative w-full aspect-[4/3] shrink-0 overflow-hidden rounded-t-lg">
            {videoSrc && !videoFailed ? (
              <>
                {videoLoading && !imageSrc && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/30">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
                  </div>
                )}
                <video
                  src={videoSrc}
                  poster={imageSrc || undefined}
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload={imageSrc ? "metadata" : "auto"}
                  onLoadedData={() => setVideoLoading(false)}
                  onError={() => { setVideoLoading(false); setVideoFailed(true); }}
                />
              </>
            ) : imageSrc ? (
              <Image
                src={imageSrc}
                alt={template.title}
                fill
                className="object-cover"
                unoptimized
                sizes="(max-width: 768px) 100vw, 512px"
              />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />

            {/* Badges over image */}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              <Badge className="border-0 bg-black/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-xl">
                {typeLabel}
              </Badge>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-5 pb-6 space-y-5">
          {/* Header */}
          <DialogHeader className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {libraryMeta.modelLabel && !isEffect && (
                // Hide the model badge for effect/motion templates — those run
                // through the Higgsfield wavespeed-motion backend, which is an
                // implementation detail; surfacing "WAVESPEED-MOTION" on the
                // card is noise for the user.
                <Badge className="rounded-full border border-primary/30 bg-primary/10 px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                  {libraryMeta.modelLabel}
                </Badge>
              )}
              {templateTags.slice(0, 3).map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground"
                >
                  {tag}
                </Badge>
              ))}
            </div>
            <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
              {template.title}
            </DialogTitle>
            {template.description && (
              <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                {template.description}
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Prompt */}
          {promptText && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Промпт
                </h3>
                {canExpandPrompt && (
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setIsPromptExpanded(v => !v)}
                  >
                    {isPromptExpanded ? "Свернуть" : "Развернуть"}
                  </button>
                )}
              </div>
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] p-4">
                <p className={cn(
                  "whitespace-pre-wrap break-words font-mono text-[13px] leading-6 text-foreground/80",
                  isPromptExpanded ? "max-h-[40vh] overflow-y-auto" : "max-h-[120px] overflow-hidden"
                )}>
                  {promptText}
                </p>
                {!isPromptExpanded && canExpandPrompt && (
                  <div className="pointer-events-none absolute inset-x-4 bottom-4 h-12 bg-gradient-to-t from-background to-transparent" />
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2.5 pt-2">
            <Button
              className="h-12 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold shadow-lg"
              onClick={() => onUse(template)}
            >
              <Zap className="mr-2 h-4 w-4" />
              {isEffect ? "Запустить" : useLabel}
              {/* Cover sheet doesn't run anything — clicking the CTA navigates
                  to the next step (photo upload / prompt editor) where the
                  real price lives in the GenerateBar. Showing the cost here
                  too just duplicates the number and confuses the user. */}
            </Button>
            {hasRealPrompt && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 h-10 rounded-xl border-white/[0.08] bg-white/[0.04] text-sm"
                onClick={handleCopyPrompt}
              >
                {copied ? <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-400" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
                {copied ? "Скопировано" : "Копировать"}
              </Button>
            </div>
            )}
          </div>
        </div>
    </ResponsiveDialog>
  );
}
